import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Plus, Trophy } from 'lucide-react';
import { db } from '@/lib/db';
import type { Member, Tournament } from '@/lib/db/types';
import { Chip } from '@/components/shared/Chip';
import { TournamentCard } from '@/components/events/TournamentCard';
import { ComingSoonNotice } from '@/components/shared/ComingSoonNotice';
import { CreateTournamentSheet } from '@/components/events/CreateTournamentSheet';
import { TournamentDetailSheet } from '@/components/events/TournamentDetailSheet';

type Filter = 'all' | 'open' | 'live' | 'mine';

interface Props {
  member: Member;
  openCreateOnMount?: boolean;
  onCreateOpened?: () => void;
}

function isVisibleTournament(t: Tournament, memberId: string, joinedIds: string[]): boolean {
  const visibility = (t.config as { visibility?: string } | undefined)?.visibility;
  if (visibility !== 'private') return true;
  if (t.organizer_id === memberId) return true;
  return joinedIds.includes(t.id);
}

export function EventsTab({ member, openCreateOnMount, onCreateOpened }: Props) {
  const [events, setEvents] = useState<Tournament[]>([]);
  const [joinedIds, setJoinedIds] = useState<string[]>([]);
  const [courseNames, setCourseNames] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [key, setKey] = useState(0);

  const load = async () => {
    setLoading(true);
    const [data, joined, courses] = await Promise.all([
      db.tournaments.list(),
      db.tournaments.myJoinedIds(),
      db.courses.list(),
    ]);
    setEvents(data);
    setJoinedIds(joined);
    setCourseNames(Object.fromEntries(courses.map((c) => [c.id, c.name])));
    setLoading(false);
  };

  useEffect(() => { void load(); }, [key]);

  useEffect(() => {
    if (openCreateOnMount) {
      setOpenCreate(true);
      onCreateOpened?.();
    }
  }, [openCreateOnMount, onCreateOpened]);

  const list = useMemo(
    () => events.filter((e) => e.status !== 'cancelled' && isVisibleTournament(e, member.id, joinedIds)),
    [events, member.id, joinedIds],
  );

  const visible = useMemo(() => {
    if (filter === 'open') return list.filter((e) => e.status === 'open');
    if (filter === 'live') return list.filter((e) => e.status === 'in-progress');
    if (filter === 'mine') return list.filter((e) => joinedIds.includes(e.id) || e.organizer_id === member.id);
    return list;
  }, [list, filter, joinedIds, member.id]);

  const stats = useMemo(() => ({
    total: list.length,
    open: list.filter((e) => e.status === 'open').length,
    joined: joinedIds.filter((id) => list.some((e) => e.id === id)).length,
  }), [list, joinedIds]);

  const emptyCopy = filter === 'mine'
    ? 'You have not joined any tournaments yet.'
    : filter === 'open'
      ? 'No tournaments are open for signups right now.'
      : filter === 'live'
        ? 'No live tournaments at the moment.'
        : 'No tournaments to show. Start one below.';

  return (
    <div className="space-y-3.5 pt-4">
      <ComingSoonNotice
        title="Coming soon"
        description="Tournaments and club events are being polished for Phase 2. Browse below for a preview — full signups and live play arrive soon."
      />

      <button
        type="button"
        onClick={() => setOpenCreate(true)}
        className="relative flex w-full items-center gap-3.5 overflow-hidden rounded-2xl bg-navy p-4 text-left text-paper shadow-bt-md"
      >
        <div className="pointer-events-none absolute -right-4 -top-4 opacity-[0.08]">
          <Trophy size={110} />
        </div>
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold text-navy">
          <Plus size={22} strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-serif text-base font-bold tracking-[-0.015em]">Start a tournament</div>
          <div className="mt-0.5 text-xs text-white/70">Pick a format, set the pot, open signups</div>
        </div>
        <ChevronRight size={18} className="shrink-0 text-gold" />
      </button>

      <div className="flex gap-2">
        <div className="flex-1 rounded-xl bg-paper p-3 text-center shadow-bt">
          <div className="font-serif text-xl font-bold tracking-[-0.025em] text-ink">{stats.total}</div>
          <div className="text-[10px] font-semibold text-mute">TOTAL</div>
        </div>
        <div className="flex-1 rounded-xl bg-paper p-3 text-center shadow-bt">
          <div className="font-serif text-xl font-bold tracking-[-0.025em] text-gold-deep">{stats.open}</div>
          <div className="text-[10px] font-semibold text-mute">OPEN</div>
        </div>
        <div className="flex-1 rounded-xl bg-paper p-3 text-center shadow-bt">
          <div className="font-serif text-xl font-bold tracking-[-0.025em] text-green-700">{stats.joined}</div>
          <div className="text-[10px] font-semibold text-mute">JOINED</div>
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {([['all', 'All'], ['open', 'Open Signups'], ['live', 'Live Now'], ['mine', 'My Events']] as const).map(([id, label]) => (
          <Chip key={id} active={filter === id} onClick={() => setFilter(id)}>{label}</Chip>
        ))}
      </div>

      {loading && <div className="text-sm text-mute">Loading events…</div>}
      {!loading && visible.length === 0 && (
        <div className="rounded-xl border border-line bg-paper p-6 text-center text-sm text-ink-soft shadow-bt">{emptyCopy}</div>
      )}
      <div className="space-y-3">
        {visible.map((event) => (
          <TournamentCard
            key={event.id}
            event={event}
            courseName={event.course_id ? courseNames[event.course_id] : undefined}
            joined={joinedIds.includes(event.id)}
            onOpen={() => setDetailId(event.id)}
          />
        ))}
      </div>

      {openCreate && (
        <CreateTournamentSheet
          member={member}
          onClose={() => setOpenCreate(false)}
          onCreated={(id) => { setKey((k) => k + 1); setDetailId(id); }}
        />
      )}
      {detailId && (
        <TournamentDetailSheet
          tournamentId={detailId}
          member={member}
          courseNames={courseNames}
          onClose={() => setDetailId(null)}
          onUpdated={() => setKey((k) => k + 1)}
        />
      )}
    </div>
  );
}
